import {Component, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
    faEye,
    faEyeSlash,
    faSquareCheck,
    faSquarePlus,
    faSquareXmark,
    faUnlockKeyhole
} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs';
import {ApiService} from '../../services/api.service';
import {AuthService} from '../../services/auth.service';
import {validateAllFormFields} from '../../../common/utils';

/** P22-09: Super User internal-org lookup for Sales Manager Add Staff. */
export type InternalOrgLookupStatus =
    | 'idle'
    | 'loading'
    | 'loaded'
    | 'failed'
    | 'missing_org_id';

@Component({
    selector: 'app-signup',
    imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule, FormsModule],
    templateUrl: './signup.component.html',
    styleUrl: './signup.component.css'
})
export class SignupComponent implements OnDestroy {
  isPasswordVisible: Boolean = false;
    isDemoStaff = false;
    demoExpiryDays = 7;
    demoExpiryOptions = [3, 7, 10, 15, 30];
    isPlatformSuperuser = false;
  signupForm: FormGroup;
  group: object  = {};
  organization: any = {};
    assignedOrganizationName = '';
    /** Authenticated Super User's org name from GET organizations/:id/ (P22-09). */
    internalOrganizationName = '';
    internalOrgLookupStatus: InternalOrgLookupStatus = 'idle';
    internalOrgLookupError = '';
    /** Last customer-org selection while using the dropdown (P22-09 role switches). */
    selectedCustomerOrganization = '';
    isSalesManagerRole = false;
    private roleSubscription?: Subscription;
    private organizationGuardSubscription?: Subscription;
    private internalOrgLookupSubscription?: Subscription;
    private internalOrgLookupInFlight = false;


  constructor(private http: HttpClient,
              private router: Router,
              private route: ActivatedRoute,
              library: FaIconLibrary,
              private toastr: ToastrService,
              fb: FormBuilder,
              private apiService: ApiService,
              private authService: AuthService,
  ) {
    library.addIcons(faUnlockKeyhole,
                    faSquareCheck,
                    faEyeSlash,
                    faEye,
                    faSquarePlus,
                    faSquareXmark
                  );

      this.isPlatformSuperuser = this.authService.isSuperUser();

    // Form must exist before groups()/organizations() subscribe callbacks patch it
    // (sync observables and fast HTTP responses can emit during construction).
    this.signupForm = fb.group({
      userName: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: [''],
      email: ['', Validators.required],
      mobileNumber: ['', [Validators.required,
                          Validators.minLength(10),
                          Validators.maxLength(14),
      ]],
      groups: [[], Validators.required],
      organization: ['', Validators.required],
    });

      this.roleSubscription = this.signupForm.get('groups')!.valueChanges.subscribe((role) => {
          this.applyRoleOrganizationBehavior(role);
      });
      // Keep Sales Manager organization control aligned with lookup result only
      // (blocks stale customer values written after role selection).
      this.organizationGuardSubscription = this.signupForm.get('organization')!.valueChanges.subscribe(
          (value) => this.enforceSalesManagerOrganizationControl(value),
      );

      this.groups();
      this.organizations();
      if (this.isPlatformSuperuser) {
          this.loadInternalOrganizationName();
      }
      this.isDemoStaff = this.route.snapshot.queryParamMap.get('demo') === 'true' && this.isPlatformSuperuser;
      this.route.queryParamMap.subscribe((params) => {
          const demoMode = params.get('demo') === 'true' && this.isPlatformSuperuser;
          if (demoMode !== this.isDemoStaff) {
              this.isDemoStaff = demoMode;
              this.groups();
          }
      });

  }

    ngOnDestroy(): void {
        this.roleSubscription?.unsubscribe();
        this.organizationGuardSubscription?.unsubscribe();
        this.internalOrgLookupSubscription?.unsubscribe();
    }

    private enforceSalesManagerOrganizationControl(value: unknown): void {
        if (!this.isSalesManagerRole) {
            return;
        }
        if (this.internalOrgLookupStatus === 'loaded' && this.internalOrganizationName) {
            if (value !== this.internalOrganizationName) {
                this.signupForm.patchValue(
                    {organization: this.internalOrganizationName},
                    {emitEvent: false},
                );
            }
            return;
        }
        if (value) {
            this.signupForm.patchValue({organization: ''}, {emitEvent: false});
        }
    }

    /** Submit blocked for Sales Manager until internal org lookup succeeds. */
    get isSalesManagerSubmitBlocked(): boolean {
        return this.isSalesManagerRole && this.internalOrgLookupStatus !== 'loaded';
    }

    private isSalesManagerSelected(role: unknown): boolean {
        if (Array.isArray(role)) {
            return role.includes('Sales Manager');
        }
        return role === 'Sales Manager';
    }

    private resolveAuthenticatedOrgId(): number | null {
        const orgIdRaw = this.authService.getCurrentUserInfo()?.orgId;
        const orgId = Number(orgIdRaw);
        if (!Number.isFinite(orgId) || orgId <= 0) {
            return null;
        }
        return orgId;
    }

    private applyInternalOrganizationToForm(): void {
        if (!this.isSalesManagerRole || !this.internalOrganizationName) {
            return;
        }
        this.signupForm.patchValue(
            {organization: this.internalOrganizationName},
            {emitEvent: false},
        );
    }

    private clearSalesManagerOrganizationField(): void {
        // Prevent a prior customer org name from remaining in the control while SM is selected.
        this.signupForm.patchValue({organization: ''}, {emitEvent: false});
    }

    private applyRoleOrganizationBehavior(role: unknown): void {
        const wasSalesManager = this.isSalesManagerRole;
        this.isSalesManagerRole = this.isPlatformSuperuser && this.isSalesManagerSelected(role);

        if (!this.isPlatformSuperuser) {
            return;
        }

        if (this.isSalesManagerRole) {
            if (!wasSalesManager) {
                const current = this.signupForm.get('organization')?.value;
                if (
                    current
                    && current !== this.internalOrganizationName
                ) {
                    this.selectedCustomerOrganization = current;
                }
            }

            if (this.internalOrgLookupStatus === 'loaded' && this.internalOrganizationName) {
                this.applyInternalOrganizationToForm();
            } else {
                this.clearSalesManagerOrganizationField();
                if (
                    this.internalOrgLookupStatus === 'idle'
                    || this.internalOrgLookupStatus === 'failed'
                    || this.internalOrgLookupStatus === 'missing_org_id'
                ) {
                    this.loadInternalOrganizationName();
                }
            }
            return;
        }

        const restore =
            this.selectedCustomerOrganization
            || (Object.values(this.organization)[0] as string)
            || '';
        if (restore) {
            this.signupForm.patchValue({organization: restore}, {emitEvent: false});
        }
    }

    /**
     * Load Super User organization name from authenticated orgId only.
     * Never accepts user-entered organization identity.
     */
    loadInternalOrganizationName(): void {
        if (!this.isPlatformSuperuser) {
            return;
        }
        if (this.internalOrgLookupInFlight) {
            return;
        }

        const orgId = this.resolveAuthenticatedOrgId();
        if (orgId === null) {
            this.internalOrganizationName = '';
            this.internalOrgLookupStatus = 'missing_org_id';
            this.internalOrgLookupError =
                'Your organization could not be determined. Sign out and sign in again, then retry.';
            if (this.isSalesManagerRole) {
                this.clearSalesManagerOrganizationField();
                this.toastr.error(this.internalOrgLookupError, 'Error');
            }
            return;
        }

        this.internalOrgLookupInFlight = true;
        this.internalOrgLookupStatus = 'loading';
        this.internalOrgLookupError = '';
        if (this.isSalesManagerRole) {
            this.clearSalesManagerOrganizationField();
        }

        this.internalOrgLookupSubscription?.unsubscribe();
        this.internalOrgLookupSubscription = this.apiService.getOrganization(orgId).subscribe({
            next: (response: any) => {
                this.internalOrgLookupInFlight = false;
                const name = response?.name;
                if (typeof name === 'string' && name.trim()) {
                    this.internalOrganizationName = name.trim();
                    this.internalOrgLookupStatus = 'loaded';
                    this.internalOrgLookupError = '';
                    this.applyInternalOrganizationToForm();
                    return;
                }
                this.internalOrganizationName = '';
                this.internalOrgLookupStatus = 'failed';
                this.internalOrgLookupError =
                    'Your organization could not be loaded. Retry, or refresh the page.';
                if (this.isSalesManagerRole) {
                    this.clearSalesManagerOrganizationField();
                    this.toastr.error(this.internalOrgLookupError, 'Error');
                }
            },
            error: () => {
                this.internalOrgLookupInFlight = false;
                this.internalOrganizationName = '';
                this.internalOrgLookupStatus = 'failed';
                this.internalOrgLookupError =
                    'Your organization could not be loaded. Retry, or refresh the page.';
                if (this.isSalesManagerRole) {
                    this.clearSalesManagerOrganizationField();
                    this.toastr.error(this.internalOrgLookupError, 'Error');
                }
            },
        });
    }

    retryInternalOrganizationLookup(): void {
        if (!this.isPlatformSuperuser || !this.isSalesManagerRole) {
            return;
        }
        if (this.internalOrgLookupInFlight || this.internalOrgLookupStatus === 'loading') {
            return;
        }
        this.loadInternalOrganizationName();
    }

    onCustomerOrganizationChange(): void {
        if (this.isSalesManagerRole) {
            return;
        }
        const value = this.signupForm.get('organization')?.value;
        if (typeof value === 'string' && value) {
            this.selectedCustomerOrganization = value;
        }
    }

  groups(){
    this.apiService.getGroups().subscribe({
      next: (response: any) => {
          if (this.isDemoStaff) {
              this.group = Object.fromEntries(
                  Object.entries(response).filter(([, name]) => name === 'Demo User')
              );
              const demoRole = Object.values(this.group)[0];
              if (demoRole) {
                  this.signupForm.patchValue({groups: demoRole});
              }
          } else {
              this.group = Object.fromEntries(
                  Object.entries(response).filter(([, name]) => name !== 'Demo User')
              );
          }
          this.applyRoleOrganizationBehavior(this.signupForm.get('groups')?.value);
      },
      error: (err) =>{
        this.toastr.error('Error while getting role.', 'Error');
      }
    })
  }

  organizations(){
    this.apiService.organization().subscribe({
      next: (response: any) => {
        this.organization = response
        if (this.organization){
            const orgDefault = Object.values(this.organization)[0] as string;
            this.assignedOrganizationName = orgDefault;
            if (!this.selectedCustomerOrganization) {
                this.selectedCustomerOrganization = orgDefault;
            }
            if (!this.isSalesManagerRole) {
                this.signupForm.patchValue({'organization': orgDefault})
            } else if (this.internalOrgLookupStatus === 'loaded' && this.internalOrganizationName) {
                this.applyInternalOrganizationToForm();
            }
        }
      },
      error: (err) =>{
        this.toastr.error('Error while getting organization.', 'Error');
      }
    })
  }

    private assertSalesManagerOrganizationReady(): boolean {
        if (!this.isSalesManagerRole) {
            return true;
        }
        if (this.resolveAuthenticatedOrgId() === null) {
            this.internalOrgLookupStatus = 'missing_org_id';
            this.internalOrgLookupError =
                'Your organization could not be determined. Sign out and sign in again, then retry.';
            this.toastr.error(this.internalOrgLookupError, 'Error');
            return false;
        }
        if (this.internalOrgLookupStatus === 'loading' || this.internalOrgLookupInFlight) {
            this.toastr.error('Still loading your organization. Please wait.', 'Error');
            return false;
        }
        if (
            this.internalOrgLookupStatus !== 'loaded'
            || !this.internalOrganizationName
        ) {
            const message = this.internalOrgLookupError
                || 'Your organization must be loaded before creating a Sales Manager.';
            this.toastr.error(message, 'Error');
            return false;
        }
        return true;
    }

  onSubmit() {
      if (this.isSalesManagerSubmitBlocked || !this.assertSalesManagerOrganizationReady()) {
          return;
      }
    if (this.signupForm.valid){
      // Random Password
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+';
      let password = '';
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      const formValues = this.signupForm.value;
        let organizationName: string;
        if (this.isPlatformSuperuser && this.isSalesManagerRole) {
            // P22-09: never fall back to formValues.organization for Sales Manager.
            organizationName = this.internalOrganizationName;
        } else if (this.isPlatformSuperuser) {
            organizationName = formValues.organization;
        } else {
            organizationName = this.assignedOrganizationName || formValues.organization;
        }
        const requestData: any = {
        username: formValues.userName,
        first_name: formValues.firstName,
        last_name: formValues.lastName,
        email: formValues.email.toLowerCase(),
        mobile_number: formValues.mobileNumber,
        password: password,
        confirm_password: password,
        groups: Array.isArray(formValues.groups) ? formValues.groups : [formValues.groups],
          organization: organizationName
      };

        if (this.isDemoStaff) {
            requestData.is_demo_user = true;
            requestData.demo_expiry_days = this.demoExpiryDays;
        }


      this.apiService.signUp(requestData).subscribe({
        next: (response) => {
            this.toastr.success(
                this.isDemoStaff ? 'Demo staff account created successfully.' : 'New Staff added successfully.',
                'Success'
            );
          this.router.navigate(['/organization'], {state: {"activeTab": "staff"}});
        },
        error: (err) => {
          if (err['error']['non_field_errors']) {
            err['error']['non_field_errors'].forEach((element:any) => {
              this.toastr.error(element, 'Error');
            });
          }else if (err['error']['mobile_number']) {
            err['error']['mobile_number'].forEach((element:any) => {
              this.toastr.error(element, 'Error');
            });
          }else if (err['error']['organization']) {
            err['error']['organization'].forEach((element:any) => {
              this.toastr.error(element, 'Error');
            });
          }else if (err['error']['email']) {
            err['error']['email'].forEach((element:any) => {
              this.toastr.error(element, 'Error');
            });
          }else if (err['error']['username']) {
            err['error']['username'].forEach((element:any) => {
              this.toastr.error(element, 'Error');
            });
          }else if (err['error']['first_name']) {
            err['error']['first_name'].forEach((element:any) => {
              this.toastr.error(element, 'Error');
            });
          }else if (err['error']['last_name']) {
            err['error']['last_name'].forEach((element:any) => {
              this.toastr.error(element, 'Error');
            });
          }else if (err['error']['groups']) {
            err['error']['groups'].forEach((element:any) => {
              this.toastr.error(element, 'Error');
            });
          } else if (err['error']['detail']) {
              this.toastr.error(err['error']['detail'], 'Error');
          }
          else if (err['status'] !== 403 && err.status !== 0) {
              this.toastr.error(err.error?.detail || 'Request failed.', 'Error');
          }
        }
      });
    }else{
      validateAllFormFields(this.signupForm);
    }
  }

  togglePasswordVisibility(){
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  onCancel() {
    this.router.navigate(['organization'], {state: {'activeTab': 'staff'}})

  }
}
